import { ColumnMappings, StepChangeEvent, StepType } from "../../types/types";
import { AddonParams, BaseAddon } from "../base/BaseAddon";
import { MatchColumnsStep } from "../../components/MatchColumnsStep/MatchColumnsStep";
import toastService from "../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../core/toaster/Toaster_model";
import I18n from "../../../../../core/localization/I18n";
import { action, observable } from "mobx";
import { NoDataFound } from "../../components/NotFound/NoDataFound";

interface MatchColumnsAddonParams<T extends string = string> extends AddonParams<T> {
  matchColumnsStepHook?: (table: any[], rawData: any[], columns: any) => Promise<any[]>;
  autoMapHeaders?: boolean;
  mappings?: ColumnMappings;
}

export class MatchColumnsAddon<T extends string = string> extends BaseAddon<T> {
  matchColumnsStepHook?: (table: any[], rawData: any[], columns: any) => Promise<any[]>;
  onBack: () => void;
  autoMapHeaders?: boolean;
  @observable mappings: ColumnMappings;

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: MatchColumnsAddonParams<T>) {
    super("MatchColumnsAddon", [StepType.matchColumns], onNext, params);
    this.matchColumnsStepHook = params.matchColumnsStepHook;
    this.onBack = onBack;
    this.autoMapHeaders = params.autoMapHeaders;
  }

  @action
  setMappings = (mappings: Record<string, string>) => {
    this.mappings = mappings;
  };

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <MatchColumnsStep
        headerValues={state.headerValues}
        data={state.data}
        fields={this.fields}
        initialMappings={state.mappings || {}}
        onMappingChange={newMappings => this.setMappings(newMappings)}
        onContinue={() => this.handleContinue(state.data, state.headerValues, this.mappings)}
        onBack={this.onBack}
      />
    );
  }

  private hasEntityRelationColumns(mappings: Record<string, string>): boolean {
    return this.fields.some(field => {
      return field.type === "entityRelation" && mappings[field.key];
    });
  }

  private async handleContinue(data: any[], headerValues: string[], mappings: Record<string, string>): Promise<void> {
    try {
      const requiredFields = this.fields.filter(field => field.required).map(field => field.name);
      const unmappedRequired = requiredFields.filter(fieldName => !mappings[fieldName]);

      if (unmappedRequired.length > 0) {
        const fieldLabels = unmappedRequired
          .map(fieldName => {
            const field = this.fields.find(f => f.name === fieldName);
            return field?.name || fieldName;
          })
          .join(", ");

        toastService
          .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
          .setContent(<span>{I18n.t("errors.requiredFieldsNotMapped", { fields: fieldLabels })}</span>);
        return;
      }

      // transform data based on mappings
      const transformedData = data.map(row => {
        const transformedRow: Record<string, any> = {};
        Object.entries(mappings).forEach(([fieldName, headerValue]) => {
          const field = this.fields.find(f => f.name === fieldName);
          const fieldKey = field ? field.key : fieldName;

          const headerIndex = headerValues.indexOf(headerValue);
          if (headerIndex !== -1 && headerIndex < row.length) {
            transformedRow[fieldKey] = row[headerIndex];
          } else {
            transformedRow[fieldKey] = "";
          }
        });

        return transformedRow;
      });

      let processedData = transformedData;
      if (this.matchColumnsStepHook) {
        processedData = await this.matchColumnsStepHook(transformedData, data, mappings);
      }

      const hasEntityRelations = this.hasEntityRelationColumns(mappings);
      const nextStepType = hasEntityRelations ? StepType.entityRelationValidation : StepType.validateData;
      this.onNext({
        type: nextStepType,
        data: processedData,
        headerValues,
        columns: mappings
      });
    } catch (e) {
      console.error("Error in column mapping:", e);
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{e?.message || I18n.t("errors.errorProcessingMappings")}</span>);
    }
  }
}
